import * as z from 'zod'

import { SHIPPING_ADDRESS_TITLE } from '@/const/label'
import { REGEX_PATTERNS } from '@/const/regex'
import { useTextWidthConvertor } from '@/hooks/util'
import i18n from '@/lib/i18n'

import { ShippingAddressFormVO } from './type'

const t = i18n.t

const MAX_LENGTH = {
  address: 40,
  tel: 20,
  name: 100,
}

const generateAddressValidateMatchValue = (value: string) => {
  const emojiReg = value.match(REGEX_PATTERNS.emoji)
  const halfWidthReg = value.match(REGEX_PATTERNS.halfWidth.exist)

  if (!!emojiReg) {
    return t('cart.shipping.validation.emojiNotAllowed', { char: emojiReg[0] })
  }

  if (halfWidthReg) {
    return t('cart.shipping.validation.fullWidthRequired', {
      char: halfWidthReg[0],
    })
  }

  return t('cart.shipping.validation.invalidCharacters')
}

export const schema = z.object({
  zipCode: z
    .string()
    .min(1, t('cart.shipping.validation.required'))
    .regex(
      REGEX_PATTERNS.zipCode,
      t('cart.shipping.validation.invalidPostalCode')
    ),
  address1: z
    .string()
    .min(1, t('cart.shipping.validation.required'))
    .max(
      MAX_LENGTH.address,
      t('cart.shipping.validation.maxLength', { length: MAX_LENGTH.address })
    )
    .refine(
      (v) => {
        const { toFullWidth } = useTextWidthConvertor()
        const fixedValue = toFullWidth(v)
        return (
          !fixedValue.match(REGEX_PATTERNS.emoji) &&
          !fixedValue.match(REGEX_PATTERNS.halfWidth.exist)
        )
      },
      (v) => {
        const { toFullWidth } = useTextWidthConvertor()
        return {
          message: generateAddressValidateMatchValue(toFullWidth(v)),
        }
      }
    ),
  address2: z
    .string()
    .max(
      MAX_LENGTH.address,
      t('cart.shipping.validation.maxLength', { length: MAX_LENGTH.address })
    )
    .refine(
      (v) => {
        const { toFullWidth } = useTextWidthConvertor()
        const fixedValue = toFullWidth(v)
        return (
          !fixedValue.match(REGEX_PATTERNS.emoji) &&
          !fixedValue.match(REGEX_PATTERNS.halfWidth.exist)
        )
      },
      (v) => {
        const { toFullWidth } = useTextWidthConvertor()
        return {
          message: generateAddressValidateMatchValue(toFullWidth(v)),
        }
      }
    ),
  tel: z
    .string()
    .min(1, t('cart.shipping.validation.required'))
    .max(
      MAX_LENGTH.tel,
      t('cart.shipping.validation.maxLength', { length: MAX_LENGTH.tel })
    ),
  deliveryName: z
    .string()
    .min(1, t('cart.shipping.validation.required'))
    .max(
      MAX_LENGTH.name,
      t('cart.shipping.validation.maxLength', { length: MAX_LENGTH.name })
    )
    .refine(
      (v) => {
        const { toFullWidth } = useTextWidthConvertor()
        const fixedValue = toFullWidth(v)
        return (
          !fixedValue.match(REGEX_PATTERNS.emoji) &&
          !fixedValue.match(REGEX_PATTERNS.halfWidth.exist)
        )
      },
      (v) => {
        const { toFullWidth } = useTextWidthConvertor()
        return {
          message: generateAddressValidateMatchValue(toFullWidth(v)),
        }
      }
    ),
})

export const getFormLabels = () => {
  return {
    zipCode: t('cart.shipping.postalCode'),
    address1: t('cart.shipping.address') + '１',
    address2: t('cart.shipping.address') + '２',
    tel: t('cart.shipping.phone'),
    deliveryName: t('cart.shipping.name'),
  } as Record<keyof ShippingAddressFormVO, string>
}

export const formLabels: Record<keyof ShippingAddressFormVO, string> = {
  zipCode: '郵便番号',
  address1: '住所１',
  address2: '住所２',
  tel: 'TEL',
  deliveryName: SHIPPING_ADDRESS_TITLE,
}

export const placeholders: Record<keyof ShippingAddressFormVO, string> = {
  zipCode: '123-1234',
  address1: '東京都渋谷区恵比寿西1-13-7',
  address2: "KB's ARCH",
  tel: '01212345678',
  deliveryName: 'DEMO SALON',
}
